import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, Users, DollarSign } from 'lucide-react';
import { collection, query, collectionGroup, getDocs, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import LoadingState from '../shared/LoadingState';
import './Marketplace.css';

const Marketplace = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    loadMarketplace();
  }, []);

  useEffect(() => {
    filterServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedType, services]);

  const loadMarketplace = async () => {
    try {
      // Load all services from all active companies
      const servicesQuery = collectionGroup(db, 'services');
      const servicesSnapshot = await getDocs(servicesQuery);
      
      const servicesData = [];
      const companiesData = {};

      for (const doc of servicesSnapshot.docs) {
        const service = { id: doc.id, ...doc.data() };
        const companyId = doc.ref.parent.parent.id;
        
        // Get company data if not already loaded
        if (!companiesData[companyId]) {
          const companyDoc = await getDocs(query(collection(db, 'companies'), where('__name__', '==', companyId)));
          if (!companyDoc.empty) {
            const companyData = companyDoc.docs[0].data();
            if (companyData.active) {
              companiesData[companyId] = companyData;
            } else {
              continue; // Skip services from inactive companies
            }
          }
        }
        
        if (companiesData[companyId]) {
          servicesData.push({
            ...service,
            companyId,
            companyName: companiesData[companyId].name
          });
        }
      }

      setServices(servicesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading marketplace:', error);
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = services;

    if (searchTerm) {
      filtered = filtered.filter(
        (service) =>
          service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          service.companyName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter((service) => service.type === selectedType);
    }

    setFilteredServices(filtered);
  };

  const handleBooking = (service) => {
    // Redirect to booking page or open booking modal
    window.location.href = `/book/${service.companyId}/${service.id}`;
  };

  if (loading) return <LoadingState title="Carregando marketplace" subtitle="Buscando experiencias disponiveis" />;

  return (
    <div className="marketplace-container">
      <div className="marketplace-header">
        <h1>{t('nav.marketplace')}</h1>
        <p>Descubra e reserve os melhores serviços de turismo local</p>
      </div>

      <div className="marketplace-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="type-filters">
          <button
            className={selectedType === 'all' ? 'active' : ''}
            onClick={() => setSelectedType('all')}
          >
            Todos
          </button>
          <button
            className={selectedType === 'boat' ? 'active' : ''}
            onClick={() => setSelectedType('boat')}
          >
            {t('services.boat')}
          </button>
          <button
            className={selectedType === 'tour' ? 'active' : ''}
            onClick={() => setSelectedType('tour')}
          >
            {t('services.tour')}
          </button>
          <button
            className={selectedType === 'transfer' ? 'active' : ''}
            onClick={() => setSelectedType('transfer')}
          >
            {t('services.transfer')}
          </button>
        </div>
      </div>

      <div className="marketplace-grid">
        {filteredServices.length === 0 ? (
          <p className="no-results">{t('common.noData')}</p>
        ) : (
          filteredServices.map((service) => (
            <div key={`${service.companyId}-${service.id}`} className="service-card-marketplace">
              <div className="service-image">
                <div className="service-type-badge">{t(`services.${service.type}`)}</div>
              </div>
              
              <div className="service-content">
                <h3>{service.name}</h3>
                
                <div className="company-info">
                  <MapPin size={16} />
                  <span>{service.companyName}</span>
                </div>

                <div className="service-details-marketplace">
                  <div className="detail-item">
                    <Users size={16} />
                    <span>{service.capacity} pessoas</span>
                  </div>
                  <div className="detail-item price">
                    <DollarSign size={16} />
                    <span>R$ {service.price.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleBooking(service)}
                  className="btn-book"
                >
                  Reservar Agora
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Marketplace;
